/* eslint-disable react/no-this-in-sfc */
import React from 'react';
import {
  withAccess,
  action,
  Action,
  errorBoundary,
  withInject,
} from '@piwikpro/platform';
import { RouterService, useAdministrationOverlay } from '@piwikpro/router-crate';
import {
  SplashScreen,
  Stack,
  View,
  Spinner,
  Information,
  Tabs,
  Button,
  withTranslation,
  TFunction,
} from '@piwikpro/ui-components';
import { ListPreference } from '@piwikpro/user-preferences';
import { Outlet, useLocation, useParams } from '@tanstack/react-router';
import { TrackerSettings } from '@piwikpro/tracker-settings-crate';
import { Apps } from '@piwikpro/apps-crate';
import { IFeatureFlags } from '@piwikpro/feature-flags-crate';
import trackerSettingsReducer from '@piwikpro/tracker-settings-crate/redux';
import { ConnectedProps, connect } from 'react-redux';
import Rename from './Rename';
import { STATE_ROOT, AdministrationState } from '../../../redux';

const mapStateToProps = (state: AdministrationState & {
  trackerSettings: ReturnType<typeof trackerSettingsReducer>
  featureFlags: IFeatureFlags
}) => {
  const app = state[STATE_ROOT].apps.details.settings.settings;

  return {
    app: app as NonNullable<typeof app>,
    failed: state[STATE_ROOT].apps.details.settings.failed
      || state.trackerSettings.app.failed
      || state.trackerSettings.global.failed,
    isLoading: state[STATE_ROOT].apps.details.settings.isLoading
      || state.trackerSettings.app.isLoading
      || state.trackerSettings.global.isLoading,
    filters: state[STATE_ROOT].apps.list.filters,
    featureFlags: state.featureFlags,
  };
};

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  routerService: RouterService
  Title: typeof React.Component
  userPreferences: any
  accessManager: any
  apps: Apps
  trackerSettings: TrackerSettings
}

export interface Props extends InjectedProps, ReduxProps {
  appId: string
  pathname: string
  t: TFunction
}

interface TabEntity {
  id: number | string
  label: string
  value: string
  accessLevel?: string
}
const DeleteButton = action({ role: 'OWNER' })(Button);

const AppDetailsModals = () => {
  const overlay = useAdministrationOverlay();

  if (overlay === '/m/rename') {
    return <Rename />;
  }

  return null;
};

class AppDetails extends React.Component<Props, null> {
  constructor(props: Props) {
    super(props);

    this.moveToRenameView = this.moveToRenameView.bind(this);
    this.handleDeleteApp = this.handleDeleteApp.bind(this);
  }

  async componentDidMount() {
    const {
      apps,
      trackerSettings,
      appId,
    } = this.props;

    if (appId) {
      try {
        await Promise.all([
          apps.get(appId),
          trackerSettings.getDetailsForApp(appId),
        ]);

        // eslint-disable-next-line no-empty
      } catch (err) { }
    }
  }

  async componentDidUpdate(prevProps) {
    const {
      apps,
      trackerSettings,
      appId,
    } = this.props;

    if (appId && prevProps.appId !== appId) {
      try {
        await Promise.all([
          apps.get(appId),
          trackerSettings.getDetailsForApp(appId),
        ]);
        // eslint-disable-next-line no-empty
      } catch (err) { }
    }
  }

  async handleDeleteApp() {
    const {
      app,
      apps,
      routerService,
      filters,
    } = this.props;

    try {
      await apps.delete(app);
      routerService.push({ to: '/administration/apps' });
      await apps.find({ ...filters, offset: undefined });
      // eslint-disable-next-line no-empty
    } catch (err) { }
  }

  moveToRenameView() {
    const { routerService } = this.props;

    routerService.openAdministrationOverlay('/m/rename');
  }

  render() {
    const {
      isLoading,
      app,
      appId,
      Title,
      t,
      accessManager,
      failed,
      featureFlags,
      userPreferences,
      pathname,
      routerService,
    } = this.props;

    if (failed) {
      return (
        <SplashScreen type="notFound" />
      );
    }

    if (isLoading) {
      return (
        <Spinner type="centered" className="x-large" />
      );
    }

    const isOwner = accessManager.isGranted({ role: 'OWNER' });
    const isDemoApp = app?.attributes.appType === 'demo';
    const sdgAvailable = featureFlags?.sdgIntegration;
    const listPreferences = new ListPreference(userPreferences.load(), '');

    const appBasePath = `/administration/apps/${appId}`;

    const tabs: TabEntity[] = [
      {
        id: 'data-collection',
        label: t('apps.details.tabs.data-collection'),
        value: `/administration/apps/${appId}/data-collection`,
        accessLevel: 'view',
      },
      {
        id: 'privacy',
        label: t('apps.details.tabs.privacy'),
        value: `/administration/apps/${appId}/privacy`,
        accessLevel: 'view',
      },
      {
        id: 'reports',
        label: t('apps.details.tabs.reports'),
        value: `/administration/apps/${appId}/reports`,
        accessLevel: 'view',
      },
      {
        id: 'permissions',
        label: t('apps.details.tabs.permissions'),
        value: `/administration/apps/${appId}/permissions/users/1/${listPreferences.limit}`,
        accessLevel: 'manage',
      },
      ...(isDemoApp ? [] : [{
        id: 'installation',
        label: t('apps.details.tabs.installation'),
        value: `/administration/apps/${appId}/installation`,
        accessLevel: 'view',
      }]),
    ].filter(tab => accessManager.isGranted({ role: 'USER', level: tab.accessLevel }));

    if (sdgAvailable && isOwner && !isDemoApp) {
      tabs.push({
        id: 'sdg',
        label: t('apps.details.tabs.sdg'),
        value: `/administration/apps/${appId}/sdg/1/${listPreferences.limit}`,
      });
    }

    const activeTab = tabs.find((tab) => {
      const tabSegment = tab.value.replace(`${appBasePath}/`, '').split('/')[0];

      return pathname.startsWith(`${appBasePath}/${tabSegment}`);
    })?.value ?? tabs[0]?.value;

    const AppDetailsAndActions = () => (
      <View.Header
        title={app.attributes.name}
        caption={t('apps.details.subTitle', { id: app.id })}
        captionCopyValue={app.id}
      >
        <Action.Button appearance="default" text={t('apps.details.actions.rename')} onClick={this.moveToRenameView} />
        <DeleteButton appearance="default" text={t('apps.details.actions.delete')} onClick={this.handleDeleteApp} />
      </View.Header>
    );

    return (
      <Title part={app.attributes.name} important>
        <View>
          {isDemoApp ? (
            <Stack vertical spacing="wide">
              <Stack.Item fill>
                <AppDetailsAndActions />
              </Stack.Item>
              <Stack.Item fill>
                {/* styles the same as in View.Header wrapper component
                to adjust design (Information position and spacings) */}
                <div style={{
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  margin: '0 auto -18px auto',
                  maxWidth: '1280px',
                }}
                >
                  <Information type="info">{t('apps.details.information.demoApp')}</Information>
                </div>
              </Stack.Item>
            </Stack>
          )
            : (
              <AppDetailsAndActions />
            )}
          <View.Body>
            <Stack vertical horizontalAlignment="fill" spacing="wide">
              <Stack.Item>
                <Tabs
                  tabs={tabs}
                  RouterLink={null}
                  active={activeTab}
                  setActive={(tab: string) => {
                    const selected = tabs.find(item => item.value === tab);
                    switch (selected?.id) {
                      case 'data-collection':
                        routerService.push({
                          to: '/administration/apps/$appId/data-collection',
                          params: { appId },
                        });
                        break;
                      case 'privacy':
                        routerService.push({
                          to: '/administration/apps/$appId/privacy',
                          params: { appId },
                        });
                        break;
                      case 'reports':
                        routerService.push({
                          to: '/administration/apps/$appId/reports',
                          params: { appId },
                        });
                        break;
                      case 'permissions':
                        routerService.push({
                          to: '/administration/apps/$appId/permissions/users/$page/$limit',
                          params: {
                            appId,
                            page: '1',
                            limit: String(listPreferences.limit),
                          },
                        });
                        break;
                      case 'installation':
                        routerService.push({
                          to: '/administration/apps/$appId/installation',
                          params: { appId },
                        });
                        break;
                      case 'sdg':
                        routerService.push({
                          to: '/administration/apps/$appId/sdg/$sdgPage/$sdgLimit',
                          params: {
                            appId,
                            sdgPage: '1',
                            sdgLimit: String(listPreferences.limit),
                          },
                        });
                        break;
                      default:
                        break;
                    }
                  }}
                />
              </Stack.Item>
              <Stack.Item>
                <Outlet />
                <AppDetailsModals />
              </Stack.Item>
            </Stack>
          </View.Body>
        </View>
      </Title>
    );
  }
}

const AppDetailsConnected = connector(withInject<InjectedProps>({
  apps: 'AppsCrate.apps',
  trackerSettings: 'TrackerSettingsCrate.tracker',
  config: 'config',
  Title: 'TitleCrate.components.Title',
  userPreferences: 'userPreferences',
  routerService: 'RouterCrate.router',
})(
  withAccess(withTranslation(['administration', 'consent-settings', 'container-settings'])(errorBoundary(AppDetails as any))),
));

const AppDetailsWrapper = (props) => {
  const params = useParams({ from: '/_secured/administration/apps/$appId' });
  const { pathname } = useLocation();

  return (
    <AppDetailsConnected
      {...props}
      appId={params.appId}
      pathname={pathname}
    />
  );
};

export default AppDetailsWrapper;
